<script setup>
import Button from '/app/components/ui/button/index.vue';
import GamedashDialog from '/app/components/game-dash/Dialog.vue';
import CrossRouterLink from '/app/components/CrossRouterLink.vue';
import CreateChallengeIcon from '/app/components/home/CreateChallengeIcon.vue';
import { Icon } from '@bignerve/ui-core';
import { cn } from '/app/lib/utils';
import { usePlatform, useContextAccount, usePlatformUtils } from '/app/composables/platform';
import { useLinks as useAuthLinks, useAuth } from '/app/composables/auth';
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useShadowMode, useStaffMode } from '/app/composables/impersonate';
import { config } from '/app/config';
import NIcon from '/app/components/NIcon.vue';
import NLoading from '/app/components/NLoading.vue';
import NAvatar from '/app/components/NAvatar.vue';
import NTip from '/app/components/NTip.vue';
import NNavbarNotification from '/app/components/NNavbarNotification.vue';
import vClickOutside from '/app/directives/click-outside';
import { useBus } from '/app/composables/bus';
import { useEscapeStack } from '/app/composables/escape-stack';
import { BELL_FLY_TARGET, DASH_LANDED, POINTS_EARNED } from '/app/constant/game-dash';
import { prefersReducedMotion } from '/app/utils/motion';

const bignerveUrl = config.app.baseURL;
const route = useRoute();
const { auth: $auth } = useAuth();

const {
    signupURL,
    loginURL,
    signout,
    isHome,
} = useAuthLinks();
const opened = ref(false);
const { platform, currentPlatform, isBignerve, title, isSubplatform } = usePlatform();
const { isUserPlatformView } = usePlatformUtils();
const contextAccount = useContextAccount();
const contextUser = computed(() => contextAccount.value || {});
const account = contextAccount;
const currentUser = computed(() => $auth.current);
const { hasShadowUser, stopShadowMode } = useShadowMode();
const { hasStaffUser, stopStaffMode } = useStaffMode();
const toggle = () => {
    opened.value = !opened.value;
};

// The bell rings when something lands on it - a points bubble, or the Game Dash
// itself collapsing back into the button. Driven by the arrival, not by the
// score query, so the reaction is in sync with what the user just saw travel.
const RING_MS = 600;
const ringing = ref(false);
let ringTimeout = null;

const { on: onBus } = useBus();

const ring = () => {
    if (prefersReducedMotion()) {
        return;
    }

    clearTimeout(ringTimeout);

    // Drop the class for a frame so a second landing replays the animation
    // instead of being ignored as already-running.
    ringing.value = false;

    requestAnimationFrame(() => {
        ringing.value = true;
        ringTimeout = setTimeout(() => {
            ringing.value = false;
        }, RING_MS);
    });
};

onBus(POINTS_EARNED, ring);
onBus(DASH_LANDED, ring);

const close = () => {
    opened.value = false;
};

const scrollToTop = () => {
    window.scrollTo(0, 0);
};

useEscapeStack(opened, close);

const platformSlug = computed(() => {
    return route.params.platformSlug
        || (isUserPlatformView.value ? currentPlatform.value?.slug : null)
        || undefined
});

watch(route, () => {
    opened.value = false;
}, { immediate: true });
</script>

<template>
    <div id="app-navbar" class="app-navbar shadow-strong z-[800] !fixed w-full"
        :class="cn(hasShadowUser && 'shadow-red-400', hasStaffUser && 'shadow-nerve-400')">
        <NNavbarNotification :immediate="hasStaffUser">
            <div class="bg-nerve-500 text-white py-3 z-[800]">
                <div class="container mx-auto flex items-center leading-none">
                    <NIcon as="exclamation-circle-solid" class="mr-2 pb-1" />
                    <span class="flex-1">
                        You are currently in <strong>Staff Mode</strong>
                        <span v-if="currentUser.user.screen_name">
                            - Logged in as: <strong>{{ currentUser.user.screen_name }}</strong>
                        </span>
                    </span>
                    <!--                     
                    <Button variant="outline" class="ml-2 border-white bg-transparent text-white" @click.prevent="stopStaffMode">
                        Stop Staff Mode
                    </Button> -->
                </div>
            </div>
        </NNavbarNotification>

        <div class="container mx-auto">
            <div class="app-navbar__inside min-h-[42px]">
                <ul class="breadcrumbs">
                    <li>
                        <a :href="bignerveUrl"
                            class="breadcrumb-item has-image flex items-center hover:!text-nerve-500">
                            <img src="/img/brain.svg" alt="A Bignerve Platform Logo" aria-hidden="true" class="!mb-0">
                            Big<span class="font-normal">Nerve</span>
                        </a>
                    </li>
                    <li v-if="isSubplatform && currentPlatform?.title">
                        <RouterLink :to="{ name: 'home.challenges' }"
                            class="breadcrumb-item has-image flex items-center hover:!text-nerve-500"
                            @click="scrollToTop">
                            <img src="/img/brain.svg" alt="A Bignerve Platform Logo" aria-hidden="true" class="!mb-0">
                            <span
                                v-html="currentPlatform.title.replace('Nerve', '<span class=\'font-normal\'>Nerve</span>')"></span>
                        </RouterLink>
                    </li>
                    <template v-else>
                        <li v-if="isHome"><span class="breadcrumb-item">Home</span></li>
                        <li v-else-if="platform?.title !== title && contextUser?.user?.screen_name">
                            <RouterLink
                                :to="{ name: 'home.user', params: { platformSlug: contextUser?.user?.screen_name || account?.user?.screen_name } }"
                                class="breadcrumb-item !text-black hover:!text-nerve-500">
                                {{ title }}
                            </RouterLink>
                        </li>
                    </template>
                </ul>
                <div class="app-navbar__nav-wrapper flex-1" v-click-outside="close" :class="{ 'is-open': opened }">
                    <button aria-label="button" class="app-navbar__toggle-btn leading-none" @click.prevent="toggle">
                        Menu
                        <span class="icon icon-caret-square-down" aria-hidden="true"></span>
                    </button>
                    <ul class="app-navbar__nav">
                        <li class="border-b border-dust-300 !mt-[0.5rem] d"></li>
                        <li class="sm:!hidden">
                            <RouterLink :to="{ name: 'home.challenges' }" class="flex items-center justify-end"
                                @click="scrollToTop">
                                <img src="/img/brain.svg" alt="A Bignerve Platform Logo" aria-hidden="true"
                                    class="!mb-0 w-[14px] mr-2">
                                <span
                                    v-html="(platform?.title || '').replace('Nerve', '<span class=\'font-normal\'>Nerve</span>')"></span>
                            </RouterLink>
                        </li>
                        <li class="non-functional is-highlighted">
                            <a href="/nerv-on-ah" title="The place where all closed challenges can be browsed"><span
                                    class="icon icon-nerv" aria-hidden="true"></span> Nerv On Ah</a>
                        </li>
                        <li non-functional class="is-highlighted">
                            <RouterLink :to="{ name: 'home.challenges' }">
                                <NIcon as="stack" class="!text-xl" left />Challenges (all)
                            </RouterLink>
                        </li>
                        <li class="is-highlighted">
                            <RouterLink :to="{ name: 'discussion-forums.topics.all', params: { platformSlug } }">
                                <NIcon as="discussion-icon" left />Forums
                            </RouterLink>
                        </li>
                        <li class="is-highlighted sm:mr-auto">
                            <RouterLink :to="{ name: 'platform.olympiad.leaderboard', params: { platformSlug } }">
                                <NIcon as="stream" left />Leaderboards
                            </RouterLink>
                        </li>

                        <template v-if="!isBignerve">
                            <li class="border-b border-dust-300 !mt-[0.5rem] sm:!hidden"></li>
                            <li class="sm:!hidden">
                                <a :href="bignerveUrl" class="flex items-center">
                                    <img src="/img/brain.svg" alt="A Bignerve Platform Logo" aria-hidden="true"
                                        class="!mb-0 w-[14px] mr-2">
                                    BigNerve Home
                                </a>
                            </li>
                        </template>

                        <li class="border-b border-dust-300 !mt-[0.5rem] sm:!hidden"></li>
                        <li non-functional>
                            <RouterLink :to="{ name: 'challenges.test-idea' }">
                                <NIcon as="lightbulb" class="!text-lg" />
                                Test Idea
                            </RouterLink>
                        </li>
                        <li class="is-highlighted">
                            <a :href="`${bignerveUrl}/nerve-boards`">
                                <NIcon as="nerve-board" left />Nerve Boards
                            </a>
                        </li>
                        <li v-if="$auth.authenticated && currentUser.user.screen_name">
                            <CrossRouterLink
                                :to="{ name: 'home.user', params: { platformSlug: currentUser.user.screen_name } }"
                                class="flex items-center">
                                <NAvatar :src="currentUser.user.avatar_url" :alt="currentUser.user.screen_name"
                                    :small="true" class="inline-flex" />
                                {{ currentUser.user.screen_name }}
                            </CrossRouterLink>
                        </li>
                        <li>
                            <RouterLink :to="{ name: 'challenges.create' }" class="gap-2">
                                <CreateChallengeIcon class="text-white invert" />
                                Create Challenge
                            </RouterLink>
                        </li>
                        <li v-if="$auth.authenticated && currentUser.user.screen_name">
                            <RouterLink
                                :to="{ name: 'dashboard.dashboard', params: { platformSlug: currentUser.user.screen_name } }">
                                <NIcon as="brain" left />My Dashboard
                            </RouterLink>
                        </li>
                        <li v-if="$auth.authenticated && currentUser.user.screen_name">
                            <RouterLink :to="{ name: 'account.my-account.main' }">
                                My Account
                            </RouterLink>
                        </li>

                        <li>
                            <GamedashDialog v-if="$auth.authenticated" #activator="{ on, hasUpdates }" @show="close">
                                <NTip :open="$auth.authenticated" title="Gamedash Notifications"
                                    target="gamedash:notifications" placement="bottom" class="inline-block">
                                    <template #description>
                                        <p>We've moved "My Game Dash" to the top of the page to enhance your experience.
                                            Now you can quickly access your score information, helpful tips, and current
                                            tournament details.</p>
                                    </template>
                                    <Button variant="icon" data-heap-id="gamedash-bell" data-heap-variant="desktop"
                                        class="border border-dust-300 relative hover:bg-dust-100 !hidden sm:!inline-flex items-center justify-center"  v-on="on">
                                        <NIcon
                                            as="bell"
                                            class="text-lg inline-block origin-top"
                                            :class="{ 'animate-bell-ring': ringing }"
                                        />
                                        <!-- Landing pad for the points dot. Mirrors the badge's
                                             position so the dot becomes the badge instead of
                                             fading out next to it. Always rendered, because the
                                             badge itself only exists once hasUpdates is on. -->
                                        <span
                                            aria-hidden="true"
                                            class="absolute h-3 w-3 top-0 right-0 -mt-1 -mr-1 pointer-events-none"
                                            :data-fly-target="BELL_FLY_TARGET"
                                        ></span>
                                        <span v-if="hasUpdates" class="flex absolute h-3 w-3 top-0 right-0 -mt-1 -mr-1">
                                            <span
                                                class="animate-ping absolute inline-flex h-full w-full rounded-full bg-nerve-400 opacity-75"></span>
                                            <span class="relative inline-flex rounded-full h-3 w-3 bg-nerve-500"></span>
                                        </span>
                                    </Button>

                                    <button class="relative flex gap-2 sm:!hidden" data-heap-id="gamedash-bell" data-heap-variant="mobile" v-on="on">
                                        <NIcon
                                            as="bell"
                                            class="text-lg inline-block origin-top"
                                            :class="{ 'animate-bell-ring': ringing }"
                                        />
                                        <!-- Landing pad for the points dot. Mirrors the badge's
                                             position so the dot becomes the badge instead of
                                             fading out next to it. Always rendered, because the
                                             badge itself only exists once hasUpdates is on. -->
                                        <span
                                            aria-hidden="true"
                                            class="absolute h-3 w-3 top-0 right-0 -mt-1 -mr-1 pointer-events-none"
                                            :data-fly-target="BELL_FLY_TARGET"
                                        ></span>
                                        <span>Game Dash</span>
                                        <span v-if="hasUpdates" class="flex absolute h-3 w-3 top-0 right-0 -mt-1 -mr-1">
                                            <span
                                                class="animate-ping absolute inline-flex h-full w-full rounded-full bg-nerve-400 opacity-75"></span>
                                            <span class="relative inline-flex rounded-full h-3 w-3 bg-nerve-500"></span>
                                        </span>
                                    </button>
                                </NTip>
                            </GamedashDialog>
                        </li>
                        <li v-if="$auth.authenticated && !hasShadowUser">
                            <a href="#" @click.prevent="signout">Sign Out</a>
                        </li>
                        <template v-if="!$auth.authenticated">
                            <li v-if="!$auth.authenticating">
                                <a :href="signupURL" class="relative">
                                    Create Account
                                </a>
                            </li>
                            <li>
                                <a :href="loginURL" class="relative">
                                    <NIcon as="user-avatar" class="!text-lg inline-block"
                                        :class="{ 'opacity-0': $auth.authenticating }" />
                                    <NLoading v-if="$auth.authenticating"
                                        class="inline-block absolute -left-[1px] -bottom-[1px]" active />
                                    <span class="ml-1">Sign In</span>
                                </a>
                            </li>
                        </template>

                        <li v-if="hasStaffUser">
                            <button aria-label="button" type="button"
                                class="flex items-center font-bold px-4 !leading-4 h-8 text-sm bg-nerve-500 !text-white rounded-full hover:bg-nerve-600 shadow"
                                @click.prevent="stopStaffMode">
                                <Icon as="user-group" class="text-xs mr-2" />
                                Stop
                            </button>
                        </li>
                        <li v-else-if="hasShadowUser">
                            <button aria-label="button" type="button"
                                class="flex items-center font-bold px-4 !leading-4 h-8 text-sm bg-red-500 !text-white rounded-full hover:bg-red-600 shadow"
                                @click.prevent="stopShadowMode">
                                <Icon as="shadow-mode" class="text-xs mr-2" />
                                Stop
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>
